import React, { forwardRef, useState, useEffect } from 'react';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  showCount?: boolean;
  maxLength?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, showCount, maxLength, className = '', id, value, onChange, ...props }, ref) => {
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const [charCount, setCharCount] = useState(0);

    useEffect(() => {
      if (typeof value === 'string') {
        setCharCount(value.length);
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCharCount(e.target.value.length);
      onChange?.(e);
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          value={value}
          onChange={handleChange}
          maxLength={maxLength}
          className={`
            block w-full rounded-lg border transition-colors duration-200
            px-3 py-2.5 text-sm resize-y min-h-[100px]
            bg-white dark:bg-secondary-800
            text-gray-900 dark:text-white
            placeholder-gray-400 dark:placeholder-gray-500
            ${
              error
                ? 'border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 dark:border-secondary-600 focus:ring-primary-500 focus:border-primary-500'
            }
            focus:outline-none focus:ring-2 focus:ring-opacity-50
            disabled:bg-gray-100 disabled:dark:bg-secondary-700 disabled:cursor-not-allowed
            ${className}
          `}
          {...props}
        />
        <div className="flex justify-between mt-1">
          <div>
            {error && (
              <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
            )}
            {hint && !error && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{hint}</p>
            )}
          </div>
          {showCount && maxLength && (
            <p className={`text-sm ${charCount >= maxLength ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
              {charCount}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
